import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { AxiosError, HttpStatusCode } from 'axios';
import { useNotificationContext } from '@/contexts/NotificationCtx';

type ErrorResponseData = {
  exceptionClass: string;
  message: string;
  requestInfo: number;
};

interface HandleErrorsProps extends React.PropsWithChildren {
  logout: () => void;
}

export function HandleErrors({ logout, children }: HandleErrorsProps) {
  const { t } = useTranslation();
  const ctxNotify = useNotificationContext();

  // Helper function to check if an object conforms to ErrorResponseData
  function isErrorResponseData(data: unknown): data is ErrorResponseData {
    return typeof data === 'object' && data !== null && 'message' in data && typeof (data as any).message === 'string';
  }

  function requestErrorHandler(error: AxiosError): Promise<unknown> {
    ctxNotify?.showNotification('error', t('general.errorhandle.timeout'), undefined, 10);
    return Promise.reject(error);
  }

  function responseErrorHandler(error: AxiosError): Promise<unknown> {
    if (error.response) {
      const statusCode = error.response.status;

      if (statusCode == HttpStatusCode.Unauthorized) {
        logout();
        ctxNotify?.showNotification('error', t('general.authorization.error'), undefined, 10);
        return Promise.reject(error);
      }

      if (statusCode == HttpStatusCode.BadRequest) {
        const responseData = error.response.data;
        let msg: string | undefined = undefined;
        let type: string | undefined = undefined;

        if (isErrorResponseData(responseData)) {
          msg = responseData.message;
          type = responseData.exceptionClass;
        }

        if (type?.includes('UserNotInDriverInputException')) {
          return Promise.reject(error);
        }

        if (type?.includes('LogbookException') && msg) {
          ctxNotify?.showNotification('error', msg, undefined, 10);
          return Promise.reject(error);
        }

        if (type?.includes('KidNotExistsException') && msg) {
          ctxNotify?.showNotification('error', msg, undefined, 10);
          return Promise.reject(error);
        }

        ctxNotify?.showNotification('error', t('general.errorhandle.badrequest'), undefined, 10);
        return Promise.reject(error);
      }

      if (statusCode == HttpStatusCode.NotFound) {
        const responseData = error.response.data;
        let type: string | undefined = undefined;

        if (isErrorResponseData(responseData)) {
          type = responseData.exceptionClass;
        }

        if (type?.includes('DriveOnAuthTemplateNotFoundException')) {
          return Promise.reject(error);
        }

        ctxNotify?.showNotification('error', t('general.errorhandle.notfound'), undefined, 10);
        return Promise.reject(error);
      }

      if (statusCode == HttpStatusCode.Conflict) {
        const responseData = error.response.data;
        let type: string | undefined = undefined;

        if (isErrorResponseData(responseData)) {
          type = responseData.exceptionClass;
        }

        if (
          type?.includes('DriveOnTargetIdentificationException') ||
          type?.includes('DriveOnTargetAlreadyAssignedException')
        ) {
          return Promise.reject(error);
        }

        ctxNotify?.showNotification('error', t('general.errorhandle.conflict'), undefined, 10);
        return Promise.reject(error);
      }
    }

    if (error.code === 'ERR_NETWORK') {
      ctxNotify?.showNotification('error', t('general.errorhandle.networkerror'));
      return Promise.reject(error);
    }

    //Todo más errorcode-oknál ha nem redirectelünk, noti dobás ?!
    ctxNotify?.showNotification('error', t('general.errorhandle.servererror'), undefined, 10);
    return Promise.reject(error);
  }

  useEffect(() => {
    axios.interceptors.request.use((config) => {
      config.timeout = 60000;
      config.withCredentials = true;
      config.withXSRFToken = true;
      return config;
    }, requestErrorHandler);

    axios.interceptors.response.use((config) => {
      return config;
    }, responseErrorHandler);

    return () => {
      axios.interceptors.request.clear();
      axios.interceptors.response.clear();
    };
  }, [t, ctxNotify]);

  return children;
}
